import { useEffect, useState } from "react";

import { useTranslation } from "_metronic/i18n/language";
import store from "app/store/store";
import { titlePage } from "utils/titlepage";
import { UserService, UserApiService, QuizService } from "app/services";
import { UserType } from "interfaces";
import { QuestionService } from "../../../../services/Ananse/QuestionService";
import { ApiQuestionType, QuizType } from "app/types";
import { ResultQuizCognitiveType } from "./CognitiveResults/cognitive-result-view";

type AccountModelProps = {
  userService: UserService;
  questionService: QuestionService;
  quizService: QuizService;
  processResultCognitive: (result: ResultQuizCognitiveType[]) => void;
};

const quizCache = {} as Record<number, QuizType>;

export const useCognitiveResultModel = ({
  userService,
  questionService,
  quizService,
  processResultCognitive,
}: AccountModelProps) => {
  const [accounts, setAccounts] = useState<unknown[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [quizNames, setQuizNames] = useState<Record<string, string>>({});

  const translate: (key: string) => string = useTranslation();
  const titleP = translate("screenApp.listPacient.account.titleList");
  const { auth } = store.getState();
  const endPoint = `/account/searchbyuserid?FilterInt=${auth.user.id}`;

  useEffect(() => {
    titlePage(`${titleP} - ${translate("labels_admin")}`);
  }, []);

  useEffect(() => {
    getUserAll();
  }, []);

  const headRows = [
    { label: translate("screens.dashboard.action") },
    { column: "Name", label: translate("screens.user.list.name") },
    { column: "Cpf", label: translate("screens.user.list.document") },
    { column: "Email", label: translate("screens.user.list.email") },
    { column: "UserId", label: translate("screens.user.list.user") },
    {
      column: "registrationDate",
      label: translate("screens.user.list.created"),
    },
  ];

  const getUserAll = async (): Promise<void> => {
    if (!UserApiService.total) {
      const total = await userService.getTotal();
      UserApiService.total = total;
    }
    const { data } = await userService.get();
    setUsers(data);
  };

  const searchUserById = (id: number): string => {
    const filterUser = users?.find(value => value.id === id);
    return filterUser?.nome ?? "";
  };

  const handleChangeAccounts = (accounts: Array<unknown>): void => {
    setAccounts(accounts);
  };

  const findQuestion = (questionId: number): Promise<ApiQuestionType> => {
    const question = questionService.get(questionId);
    return question;
  };

  const getQuiz = async (quizId: number): Promise<QuizType> => {
    try {
      if (!quizId) {
        throw new Error();
      }
      if (quizCache?.[quizId]) {
        return quizCache[quizId];
      }
      const result = await quizService.get(quizId);
      quizCache[quizId] = result;
      return quizCache[quizId];
    } catch (error) {
      console.warn("Erro ao buscar nome do quiz com id: ", quizId);
      return {} as QuizType;
    }
  };

  const addQuizName = (newQuiz: Record<string, string>): void => {
    setQuizNames(prev => ({
      ...prev,
      ...newQuiz,
    }));
  };

  const getQuizNameByTestResultId = (testResultId: string): string => {
    return quizNames?.[testResultId] || "";
  };

  if (!auth?.user?.id) {
    throw new Error("User ID is required to fetch user data");
  }

  return {
    searchUserById,
    translate,
    handleChangeAccounts,
    findQuestion,
    getQuiz,
    addQuizName,
    getQuizNameByTestResultId,
    processResultCognitive,
    accounts,
    headRows,
    endpoint: endPoint,
  };
};
